import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { deliveryRouteQueryKey, type DeliveryRouteResponse, fetchDeliveryRoute } from '@/api/delivery-route'
import { routeFetchWindow } from '@/lib/chefs/print-plan'

export interface PrintDeliveryRoute {
  /**
   * The route for the packet's Delivery route section. `undefined` = not asked
   * for (or still loading, see `pending`); `null` = no route for this packet:
   * it failed, the days span more than the endpoint serves, or overdue
   * temperature logs hold it back. A null route prints the section's band.
   */
  route: DeliveryRouteResponse | null | undefined
  /** Asked for and still loading: the caller builds no print target until it lands. */
  pending: boolean
  /** The read is on (`wanted` as of this render): a selection that turned the section on a render ago is not yet. */
  asked: boolean
}

/**
 * The delivery route the chef packet prints (`/api/kitchen-orders/delivery-route`),
 * read only while the print dialog's selection has the Delivery route ticked.
 * Read once per selection and held (`staleTime: Infinity`): the Delivery
 * page's own query refreshes every minute ("Received" lands during the drive),
 * and a packet that changed under the open print dialog would re-measure and
 * re-prepare its PDF.
 */
export function usePrintDeliveryRoute({
  kitchenLocationId,
  wanted,
  dayKeys,
}: {
  kitchenLocationId: string | null | undefined
  /** The selection has the Delivery route section on. */
  wanted: boolean
  /** The picked delivery days that have orders (`YYYY-MM-DD`). */
  dayKeys: readonly string[]
}): PrintDeliveryRoute {
  const fetchWindow = useMemo(() => (wanted ? routeFetchWindow(dayKeys) : null), [wanted, dayKeys])
  const vars = {
    kitchenLocationId: kitchenLocationId ?? '',
    startDate: fetchWindow?.startDate ?? '',
    endDate: fetchWindow?.endDate ?? '',
  }
  const query = useQuery<DeliveryRouteResponse>({
    queryKey: [...deliveryRouteQueryKey(vars), 'print'],
    queryFn: ({ signal }) => fetchDeliveryRoute(vars, signal),
    enabled: Boolean(kitchenLocationId) && fetchWindow !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
  })
  if (!wanted) {
    return { route: undefined, pending: false, asked: false }
  }
  if (!kitchenLocationId || fetchWindow === null || query.isError) {
    return { route: null, pending: false, asked: true }
  }
  if (query.isSuccess) {
    return { route: query.data.tempLogGate ? null : query.data, pending: false, asked: true }
  }
  return { route: undefined, pending: true, asked: true }
}
